import React, { useState, useEffect } from 'react';
import api, { orderAPI } from '../api';
import type { Order, OrderSearchParams } from '../types/order';
import CollapsibleFilters from './CollapsibleFilters';
import ConfirmDeleteModal from './ConfirmDeleteModal';

interface OrderListProps {
  onOrderSelect: (orderId: number, orderNumber?: string) => void;
  statusFilter?: string;
  onAddOrder?: () => void;
  onImportOrders?: () => void;
  userRole?: string;
}

// Helper: get date string for N days ago
const getDateNDaysAgo = (days: number): string => {
  const date = new Date();
  date.setDate(date.getDate() - days);
  return date.toISOString().split('T')[0];
};

// First day of the current calendar month (YYYY-MM-DD). The Orders tab defaults
// to the current month so the list matches the month-scoped dashboard tiles.
const getCurrentMonthStart = (): string => {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().split('T')[0];
};

const OrderList: React.FC<OrderListProps> = ({ 
  onOrderSelect, 
  statusFilter = '', 
  onAddOrder, 
  onImportOrders,
  userRole = 'viewer'
}) => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalOrders, setTotalOrders] = useState(0);
  
  // Search and filter state
  const [searchOrderNumber, setSearchOrderNumber] = useState('');
  const [filterStatus, setFilterStatus] = useState<string>(statusFilter || 'pending,confirmed,allocated');
  // Default the Orders tab to the current calendar month so the list matches
  // the month-scoped dashboard tiles. Historical data is available via Reports
  // & Analytics (which have full date-range pickers).
  const [filterDateFrom, setFilterDateFrom] = useState(getCurrentMonthStart());
  const [filterDateTo, setFilterDateTo] = useState('');
  const [filterChannel, setFilterChannel] = useState('');
  const [channelOptions, setChannelOptions] = useState<string[]>([]);
  
  // Sorting state
  const [sortBy, setSortBy] = useState<string>('order_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Multi-select state
  const [selectedOrderIds, setSelectedOrderIds] = useState<Set<number>>(new Set());
  const [bulkAction, setBulkAction] = useState<string>('');
  const [bulkProcessing, setBulkProcessing] = useState(false);
  const [bulkResult, setBulkResult] = useState<any>(null);

  useEffect(() => {
    fetchOrders();
  }, [currentPage, pageSize, sortBy, sortOrder, filterStatus, filterChannel]);

  // Fetch channel options for the filter dropdown
  useEffect(() => {
    const fetchChannels = async () => {
      try {
        const res = await api.get('/channels/');
        const channels = Array.isArray(res.data) ? res.data : [];
        setChannelOptions(channels.map((ch: any) => ch.name).filter(Boolean).sort());
      } catch (e) {}
    };
    fetchChannels();
  }, []);

  // Update filter when statusFilter prop changes
  useEffect(() => {
    if (statusFilter !== filterStatus) {
      setFilterStatus(statusFilter);
      setCurrentPage(1);
    }
  }, [statusFilter]);

  const fetchOrders = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params: OrderSearchParams = {
        skip: (currentPage - 1) * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      
      if (searchOrderNumber) params.q = searchOrderNumber;
      if (filterStatus) params.status = filterStatus;
      if (filterChannel) params.channel = filterChannel;
      if (filterDateFrom) params.date_from = filterDateFrom;
      if (filterDateTo) params.date_to = filterDateTo;
      
      const response = await orderAPI.listOrders(params);
      setOrders(response.data.orders || []);
      setTotalOrders(response.data.total || 0);
      setError(null); // Clear any previous errors
    } catch (err: any) {
      console.error('Error fetching orders:', err);
      
      // Handle different error formats
      let errorMessage = 'Failed to fetch orders';
      
      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;
        
        // Handle array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map((e: any) => {
            if (typeof e === 'object' && e.msg) {
              return `${e.loc?.join('.') || 'Field'}: ${e.msg}`;
            }
            return String(e);
          }).join(', ');
        }
        // Handle string error
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
        // Handle object error
        else if (typeof detail === 'object') {
          errorMessage = detail.msg || JSON.stringify(detail);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      setOrders([]);
      setTotalOrders(0);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchOrders();
  };

  const handleClearFilters = () => {
    setSearchOrderNumber('');
    setFilterStatus('pending,confirmed,allocated');
    setFilterChannel('');
    setFilterDateFrom(getCurrentMonthStart());
    setFilterDateTo('');
    setCurrentPage(1);
    setTimeout(() => fetchOrders(), 0);
  };

  // Multi-select handlers
  const toggleSelectOrder = (orderId: number, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) next.delete(orderId);
      else next.add(orderId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedOrderIds.size === orders.length) {
      setSelectedOrderIds(new Set());
    } else {
      setSelectedOrderIds(new Set(orders.map(o => o.id)));
    }
  };

  const handleBulkStatusUpdate = async (status: string) => {
    if (selectedOrderIds.size === 0) return;
    setBulkProcessing(true);
    setBulkResult(null);
    try {
      const res = await orderAPI.bulkUpdateStatus(Array.from(selectedOrderIds), status);
      setBulkResult(res.data);
      if (res.data.success_count > 0) {
        setSelectedOrderIds(new Set());
        fetchOrders();
      }
    } catch (err: any) {
      setBulkResult({ success_count: 0, failed_count: selectedOrderIds.size, results: [{ success: false, error: err.response?.data?.detail || 'Failed' }] });
    } finally {
      setBulkProcessing(false);
      setBulkAction('');
    }
  };

  const handleBulkDeallocate = async () => {
    if (selectedOrderIds.size === 0) return;
    setBulkProcessing(true);
    setBulkResult(null);
    try {
      const res = await orderAPI.bulkDeallocate(Array.from(selectedOrderIds));
      setBulkResult(res.data);
      if (res.data.success_count > 0) {
        setSelectedOrderIds(new Set());
        fetchOrders();
      }
    } catch (err: any) {
      setBulkResult({ success_count: 0, failed_count: selectedOrderIds.size, results: [{ success: false, error: err.response?.data?.detail || 'Failed' }] });
    } finally {
      setBulkProcessing(false);
      setBulkAction('');
    }
  };

  const handleBulkDelete = async () => {
    if (selectedOrderIds.size === 0) return;
    setBulkProcessing(true);
    setBulkResult(null);
    setShowBulkDeleteConfirm(false);
    try {
      const res = await orderAPI.bulkDelete(Array.from(selectedOrderIds));
      setBulkResult(res.data);
      if (res.data.success_count > 0) {
        setSelectedOrderIds(new Set());
        fetchOrders();
      }
    } catch (err: any) {
      setBulkResult({ success_count: 0, failed_count: selectedOrderIds.size, results: [{ success: false, error: err.response?.data?.detail || 'Failed' }] });
    } finally {
      setBulkProcessing(false);
      setBulkAction('');
    }
  };

  const handleBulkDeleteClick = () => {
    if (selectedOrderIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const [holdProcessingId, setHoldProcessingId] = useState<number | null>(null);

  const handleToggleHold = async (order: Order, e: React.MouseEvent) => {
    e.stopPropagation();
    setHoldProcessingId(order.id);
    try {
      await orderAPI.setHold(order.id, !order.on_hold);
      fetchOrders();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to update hold status');
    } finally {
      setHoldProcessingId(null);
    }
  };

  const handleBulkHold = async (onHold: boolean) => {
    if (selectedOrderIds.size === 0) return;
    setBulkProcessing(true);
    setBulkResult(null);
    try {
      const res = await orderAPI.bulkHold(Array.from(selectedOrderIds), onHold);
      setBulkResult(res.data);
      if (res.data.success_count > 0) {
        setSelectedOrderIds(new Set());
        fetchOrders();
      }
    } catch (err: any) {
      setBulkResult({ success_count: 0, failed_count: selectedOrderIds.size, results: [{ success: false, error: err.response?.data?.detail || 'Failed' }] });
    } finally {
      setBulkProcessing(false);
      setBulkAction('');
    }
  };

  const handleSyncStatus = async () => {
    if (selectedOrderIds.size === 0) {
      alert('Please select orders to sync status to channel.');
      return;
    }
    setBulkProcessing(true);
    setBulkResult(null);
    try {
      const res = await api.post('/orders/sync-status', { order_ids: Array.from(selectedOrderIds) });
      setBulkResult({
        success_count: res.data.pushed,
        failed_count: res.data.failed,
        skipped_count: res.data.skipped,
        message: res.data.message,
        pushed: res.data.pushed,
        sync_results: res.data.results,
      });
      fetchOrders();
    } catch (err: any) {
      setBulkResult({ success_count: 0, failed_count: selectedOrderIds.size, message: err.response?.data?.detail || 'Sync failed' });
    } finally {
      setBulkProcessing(false);
    }
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('desc');
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; bg: string; label: string }> = {
      pending: { color: '#856404', bg: '#fff3cd', label: 'Pending' },
      confirmed: { color: '#004085', bg: '#cce5ff', label: 'Confirmed' },
      allocated: { color: '#0c5460', bg: '#d1ecf1', label: 'Allocated' },
      shipped: { color: '#383d41', bg: '#d6d8db', label: 'Shipped' },
      delivered: { color: '#155724', bg: '#d4edda', label: 'Delivered' },
      cancelled: { color: '#721c24', bg: '#f8d7da', label: 'Cancelled' },
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    
    return (
      <span style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: '600',
        color: config.color,
        backgroundColor: config.bg,
        border: `1px solid ${config.color}40`,
        whiteSpace: 'nowrap',
        textTransform: 'uppercase',
        letterSpacing: '0.3px'
      }}>
        {config.label}
      </span>
    );
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatCurrency = (amount: number, currency: string = 'INR') => {
    const symbol = currency === 'INR' ? '₹' : currency;
    return `${symbol}${amount.toFixed(2)}`;
  };

  const totalPages = Math.ceil(totalOrders / pageSize);
  const startRecord = totalOrders > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRecord = Math.min(currentPage * pageSize, totalOrders);

  if (loading && orders.length === 0) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading orders...</div>;
  }

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header with global search */}
      <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid #dee2e6', flexShrink: 0, gap: '12px', flexWrap: 'wrap' }}>
        <h2 className="card-title" style={{ fontSize: '16px', margin: 0 }}>Orders</h2>
        <div style={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: '200px' }}>
          <input
            type="text"
            value={searchOrderNumber}
            onChange={(e) => setSearchOrderNumber(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            placeholder="Search order #, customer, status, channel..."
            style={{ padding: '6px 12px', border: '1px solid #dde2e8', borderRadius: '6px', fontSize: '13px', width: '100%', maxWidth: '400px' }}
          />
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          {onImportOrders && (
            <button onClick={onImportOrders} className="btn btn-secondary btn-sm">
              <span>📥</span> Import
            </button>
          )}
          {onAddOrder && (
            <button onClick={onAddOrder} className="btn btn-primary btn-sm">
              <span>➕</span> Add Order
            </button>
          )}
        </div>
      </div>

      {/* Filter bar */}
      <CollapsibleFilters label="Filters">
      <div style={{ padding: '10px 16px', background: '#f8f9fa', borderBottom: '1px solid #dee2e6', flexShrink: 0, display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid #ced4da', borderRadius: '4px' }}
        >
          <option value="">All Status</option>
          <option value="pending,confirmed,allocated">Active (Pending/Confirmed/Allocated)</option>
          <option value="pending">Pending</option>
          <option value="confirmed">Confirmed</option>
          <option value="allocated">Allocated</option>
          <option value="shipped">Shipped</option>
          <option value="delivered">Delivered</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select
          value={filterChannel}
          onChange={(e) => setFilterChannel(e.target.value)}
          style={{ padding: '6px 10px', fontSize: '13px', border: '1px solid #ced4da', borderRadius: '4px' }}
        >
          <option value="">All Channels</option>
          {channelOptions.map(ch => (
            <option key={ch} value={ch}>{ch}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: '#64748b' }}>From:</label>
          <input type="date" value={filterDateFrom} onChange={(e) => setFilterDateFrom(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px' }} />
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <label style={{ fontSize: '12px', color: '#64748b' }}>To:</label>
          <input type="date" value={filterDateTo} onChange={(e) => setFilterDateTo(e.target.value)} style={{ padding: '5px 8px', border: '1px solid #ced4da', borderRadius: '4px', fontSize: '12px' }} />
        </div>
        <button onClick={handleSearch} className="btn btn-primary btn-sm">Apply</button>
        <button onClick={handleClearFilters} className="btn btn-secondary btn-sm">Clear</button>
      </div>
      </CollapsibleFilters>

      {/* Bulk Action Bar */}
      {selectedOrderIds.size > 0 && (
        <div style={{ padding: '10px 16px', background: '#e0f2fe', borderBottom: '1px solid #7dd3fc', flexShrink: 0, display: 'flex', gap: '8px', alignItems: 'center' }}>
          <strong style={{ fontSize: '13px', color: '#0369a1' }}>{selectedOrderIds.size} selected</strong>
          <span style={{ color: '#64748b', fontSize: '12px' }}>|</span>
          <select
            value={bulkAction}
            onChange={(e) => setBulkAction(e.target.value)}
            style={{ padding: '5px 8px', fontSize: '12px', border: '1px solid #7dd3fc', borderRadius: '4px', background: 'white' }}
          >
            <option value="">Change Status...</option>
            <option value="confirmed">→ Confirmed</option>
            <option value="allocated">→ Allocated</option>
            <option value="shipped">→ Shipped</option>
            <option value="delivered">→ Delivered</option>
            <option value="cancelled">→ Cancelled</option>
          </select>
          {bulkAction && (
            <button
              onClick={() => handleBulkStatusUpdate(bulkAction)}
              disabled={bulkProcessing}
              className="btn btn-primary btn-sm"
              style={{ fontSize: '12px' }}
            >
              {bulkProcessing ? 'Processing...' : 'Apply'}
            </button>
          )}
          <button
            onClick={handleBulkDeallocate}
            disabled={bulkProcessing}
            className="btn btn-sm"
            style={{ fontSize: '12px', background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }}
          >
            Deallocate
          </button>
          <button
            onClick={() => handleBulkHold(true)}
            disabled={bulkProcessing}
            className="btn btn-sm"
            style={{ fontSize: '12px', background: '#fff3cd', color: '#856404', border: '1px solid #ffe08a' }}
            title="Flag selected orders as on hold"
          >
            Put On Hold
          </button>
          <button
            onClick={() => handleBulkHold(false)}
            disabled={bulkProcessing}
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '12px' }}
            title="Release selected orders from hold"
          >
            Release Hold
          </button>
          <button
            onClick={handleSyncStatus}
            disabled={bulkProcessing}
            className="btn btn-sm"
            style={{ fontSize: '12px', background: '#eff6ff', color: '#1e40af', border: '1px solid #bfdbfe' }}
          >
            {bulkProcessing ? 'Syncing...' : 'Sync Status'}
          </button>
          <button
            onClick={handleBulkDeleteClick}
            disabled={bulkProcessing}
            className="btn btn-sm"
            style={{ fontSize: '12px', background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
          >
            Delete
          </button>
          <button
            onClick={() => setSelectedOrderIds(new Set())}
            className="btn btn-secondary btn-sm"
            style={{ fontSize: '12px', marginLeft: 'auto' }}
          >
            Clear Selection
          </button>
        </div>
      )}

      {/* Bulk Result */}
      {bulkResult && (
        <div style={{ padding: '10px 16px', background: bulkResult.failed_count === 0 ? '#d1fae5' : '#fef3c7', borderBottom: '1px solid #6ee7b7', flexShrink: 0, fontSize: '13px' }}>
          {bulkResult.message ? (
            <strong>{bulkResult.message}</strong>
          ) : (
            <><strong>{bulkResult.success_count} succeeded</strong>{bulkResult.failed_count > 0 && <span>, <strong style={{ color: '#dc2626' }}>{bulkResult.failed_count} failed</strong></span>}</>
          )}
          {bulkResult.pushed > 0 && <span style={{ marginLeft: '8px', color: '#0d9488' }}>({bulkResult.pushed} pushed to channel)</span>}
          {bulkResult.skipped_count > 0 && <span style={{ marginLeft: '8px', color: '#64748b' }}>({bulkResult.skipped_count} skipped)</span>}
          {/* Detailed sync results for delivered/pushed orders */}
          {bulkResult.sync_results && bulkResult.sync_results.length > 0 && (
            <details style={{ marginTop: '6px' }}>
              <summary style={{ cursor: 'pointer', fontSize: '12px', color: '#334155', fontWeight: 500 }}>View details ({bulkResult.sync_results.length} orders)</summary>
              <div style={{ marginTop: '6px', maxHeight: '200px', overflow: 'auto', border: '1px solid #e2e8f0', borderRadius: '6px' }}>
                <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
                  <thead><tr style={{ background: '#f8fafb' }}>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Order</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>AWB</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '5px 8px', textAlign: 'left' }}>Result</th>
                  </tr></thead>
                  <tbody>
                    {bulkResult.sync_results.map((r: any, idx: number) => (
                      <tr key={idx} style={{ borderTop: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.order_number}</td>
                        <td style={{ padding: '4px 8px', fontFamily: 'monospace' }}>{r.awb_number || '—'}</td>
                        <td style={{ padding: '4px 8px' }}>{r.shipping_status || '—'} {r.pushed_status ? `→ ${r.pushed_status}` : ''}</td>
                        <td style={{ padding: '4px 8px' }}>
                          <span style={{ padding: '1px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 600, background: r.status === 'pushed' ? '#d4edda' : r.status === 'skipped' ? '#f8f9fa' : '#f8d7da', color: r.status === 'pushed' ? '#155724' : r.status === 'skipped' ? '#64748b' : '#721c24' }}>
                            {r.status}
                          </span>
                          {r.reason && <span style={{ marginLeft: '4px', color: '#64748b' }}>{r.reason}</span>}
                          {r.error && <span style={{ marginLeft: '4px', color: '#dc2626' }}>{r.error}</span>}
                          {r.channel && <span style={{ marginLeft: '4px', color: '#0d9488' }}>→ {r.channel}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
          {bulkResult.dc_numbers?.length > 0 && <span> | DCs: {bulkResult.dc_numbers.join(', ')}</span>}
          {bulkResult.results?.filter((r: any) => r.success && r.dc_id).map((r: any) => (
            <button
              key={r.dc_id}
              onClick={async () => {
                try {
                  const res = await api.get(`/delivery-challans/${r.dc_id}/pdf`, { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
                  window.open(url, '_blank');
                } catch (e) { alert('Failed to load PDF'); }
              }}
              style={{ marginLeft: '6px', color: '#0d9488', fontWeight: 600, textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none', fontSize: '12px' }}
            >
              📄 {r.dc_number}
            </button>
          ))}
          <button onClick={() => setBulkResult(null)} style={{ marginLeft: '12px', border: 'none', background: 'none', cursor: 'pointer', fontSize: '12px', color: '#64748b' }}>✕ Dismiss</button>
        </div>
      )}

      {error && (
        <div style={{ padding: '12px 16px', background: '#f8d7da', color: '#721c24', fontSize: '13px', flexShrink: 0 }}>
          {error}
        </div>
      )}

      {/* Full-Width Scrollable Table */}
      <div style={{ flex: 1, overflow: 'auto', background: 'white' }}>
        <table style={{ 
          width: '100%', 
          borderCollapse: 'collapse', 
          fontSize: '13px',
          minWidth: '900px' // Ensures horizontal scroll
        }}>
          <thead>
            <tr style={{ 
              background: '#f1f3f5', 
              borderBottom: '2px solid #dee2e6',
              position: 'sticky',
              top: 0,
              zIndex: 10
            }}>
              <th style={{ ...headerStyle, width: '40px', cursor: 'default' }}>
                <input
                  type="checkbox"
                  checked={orders.length > 0 && selectedOrderIds.size === orders.length}
                  onChange={toggleSelectAll}
                  style={{ cursor: 'pointer' }}
                />
              </th>
              <th style={headerStyle} onClick={() => handleSort('order_number')}>
                Order No {sortBy === 'order_number' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={headerStyle} onClick={() => handleSort('external_order_id')}>
                Ext Order No {sortBy === 'external_order_id' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={headerStyle} onClick={() => handleSort('channel')}>
                Channel {sortBy === 'channel' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={headerStyle} onClick={() => handleSort('order_date')}>
                Order Date {sortBy === 'order_date' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={headerStyle}>
                Delivered Date
              </th>
              <th style={headerStyle} onClick={() => handleSort('total_amount')}>
                Order Amount {sortBy === 'total_amount' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={headerStyle} onClick={() => handleSort('status')}>
                Status {sortBy === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
              </th>
              <th style={{ ...headerStyle, position: 'sticky', right: 0, background: '#f1f3f5', boxShadow: '-2px 0 4px rgba(0,0,0,0.1)' }}>
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {orders.length === 0 ? (
              <tr>
                <td colSpan={9} style={{ textAlign: 'center', padding: '3rem', color: '#6c757d' }}>
                  No orders found
                </td>
              </tr>
            ) : (
              orders.map((order, index) => (
                <tr 
                  key={order.id}
                  onClick={() => onOrderSelect(order.id, order.order_number)}
                  style={{ 
                    borderBottom: '1px solid #e9ecef',
                    background: selectedOrderIds.has(order.id) ? '#dbeafe' : (index % 2 === 0 ? 'white' : '#f8f9fa'),
                    cursor: 'pointer'
                  }}
                >
                  <td style={{ ...cellStyle, width: '40px' }} onClick={(e) => toggleSelectOrder(order.id, e)}>
                    <input
                      type="checkbox"
                      checked={selectedOrderIds.has(order.id)}
                      onChange={() => {}}
                      style={{ cursor: 'pointer' }}
                    />
                  </td>
                  <td style={cellStyle}>
                    <strong 
                      style={{ color: '#0d9488' }}
                    >
                      {order.order_number}
                    </strong>
                  </td>
                  <td style={cellStyle}>{order.external_order_id || '-'}</td>
                  <td style={cellStyle}>{order.channel || '-'}</td>
                  <td style={cellStyle}>{formatDate(order.order_date)}</td>
                  <td style={cellStyle}>{(order as any).delivered_date ? formatDate((order as any).delivered_date) : '—'}</td>
                  <td style={{ ...cellStyle, fontWeight: '600' }}>
                    {formatCurrency(order.total_amount, order.currency || undefined)}
                  </td>
                  <td style={cellStyle}>
                    <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                      {getStatusBadge(order.status || 'pending')}
                      {order.on_hold && (
                        <span
                          title="This order is on hold"
                          style={{
                            display: 'inline-block',
                            padding: '4px 10px',
                            borderRadius: '12px',
                            fontSize: '11px',
                            fontWeight: 600,
                            color: '#856404',
                            backgroundColor: '#fff3cd',
                            border: '1px solid #ffe08a',
                            whiteSpace: 'nowrap',
                            textTransform: 'uppercase',
                            letterSpacing: '0.3px',
                          }}
                        >
                          On Hold
                        </span>
                      )}
                    </div>
                  </td>
                  <td style={{ 
                    ...cellStyle, 
                    position: 'sticky', 
                    right: 0, 
                    background: selectedOrderIds.has(order.id) ? '#dbeafe' : (index % 2 === 0 ? 'white' : '#f8f9fa'),
                    boxShadow: '-2px 0 4px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', gap: '6px', alignItems: 'center', justifyContent: 'flex-end' }}>
                      {(order.on_hold || !['shipped', 'delivered', 'cancelled'].includes(order.status || '')) && (
                        <button
                          onClick={(e) => handleToggleHold(order, e)}
                          disabled={holdProcessingId === order.id}
                          title={order.on_hold ? 'Release this order from hold' : 'Put this order on hold'}
                          style={{
                            padding: '4px 10px',
                            fontSize: '12px',
                            fontWeight: '600',
                            background: order.on_hold ? 'white' : '#fff3cd',
                            color: order.on_hold ? '#334155' : '#856404',
                            border: order.on_hold ? '1px solid #e2e8f0' : '1px solid #ffe08a',
                            borderRadius: '4px',
                            cursor: holdProcessingId === order.id ? 'not-allowed' : 'pointer',
                            opacity: holdProcessingId === order.id ? 0.5 : 1,
                            whiteSpace: 'nowrap'
                          }}
                        >
                          {order.on_hold ? 'Release' : 'Hold'}
                        </button>
                      )}
                      <button
                        onClick={(e) => { e.stopPropagation(); onOrderSelect(order.id, order.order_number); }}
                        style={{
                          padding: '4px 12px',
                          fontSize: '12px',
                          fontWeight: '600',
                          background: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Footer */}
      <div style={{ 
        padding: '12px 16px', 
        background: '#f8f9fa', 
        borderTop: '1px solid #dee2e6',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexShrink: 0,
        fontSize: '13px'
      }}>
        <div style={{ color: '#495057' }}>
          Showing {startRecord} to {endRecord} of {totalOrders} orders
        </div>
        
        <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
          <span style={{ color: '#495057' }}>Rows per page:</span>
          <select 
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setCurrentPage(1);
            }}
            style={{ 
              padding: '4px 8px', 
              fontSize: '13px', 
              border: '1px solid #ced4da', 
              borderRadius: '4px' 
            }}
          >
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
            <option value={200}>200</option>
          </select>
          
          <button 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(currentPage - 1)}
            style={{ 
              padding: '4px 12px', 
              fontSize: '13px', 
              background: currentPage === 1 ? '#e9ecef' : '#007bff',
              color: currentPage === 1 ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '4px', 
              cursor: currentPage === 1 ? 'not-allowed' : 'pointer'
            }}
          >
            Previous
          </button>
          
          <span style={{ color: '#495057', fontWeight: '600' }}>
            Page {currentPage} of {totalPages || 1}
          </span>
          
          <button 
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(currentPage + 1)}
            style={{ 
              padding: '4px 12px', 
              fontSize: '13px', 
              background: (currentPage === totalPages || totalPages === 0) ? '#e9ecef' : '#007bff',
              color: (currentPage === totalPages || totalPages === 0) ? '#6c757d' : 'white',
              border: 'none', 
              borderRadius: '4px', 
              cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer'
            }}
          >
            Next
          </button>
        </div>
      </div>

      <ConfirmDeleteModal
        isOpen={showBulkDeleteConfirm}
        title="Delete Orders"
        message={`Are you sure you want to delete ${selectedOrderIds.size} order(s)? This action cannot be undone.`}
        requireTypedConfirmation={selectedOrderIds.size > 5}
        confirmationWord="confirm"
        onConfirm={handleBulkDelete}
        onCancel={() => setShowBulkDeleteConfirm(false)}
      />
    </div>
  );
};

const headerStyle: React.CSSProperties = {
  padding: '10px 12px',
  textAlign: 'left',
  fontWeight: '700',
  fontSize: '11px',
  color: '#495057',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  borderRight: '1px solid #dee2e6'
};

const cellStyle: React.CSSProperties = {
  padding: '10px 12px',
  whiteSpace: 'nowrap',
  borderRight: '1px solid #e9ecef'
};

export default OrderList;
